/**
 * A draft's messages and the active message's polling
 * (docs/plans/lms-course-editor-plan.md 4.10, 8.2, 8.5, 18.3, 18.5; WP-E30):
 *
 * - GET /admin/builder/versions/:versionId/edits (every page, ordinal
 *   ascending) on mount, on a new version id and on refresh().
 * - The active message is the newest one whose run is still working
 *   (uploaded, extracting, queued, structuring to writing). It is read with
 *   GET /admin/builder/edits/:messageId every 5 seconds while the page is
 *   visible (document.visibilityState, LMS plan D14): a hidden tab asks
 *   nothing and a tab that becomes visible asks at once. A message waiting at
 *   "estimated" for the admin's Send is not polled. Polling, never SSE.
 * - When a polled message stops working (applied, clarify, declined,
 *   no_change, failed, cancelled, or ready for its second Send), onSettled is
 *   called once with the new message and its previous state; the panel
 *   reloads the draft when it was applied (8.5).
 * - upsert() takes the answer of an action (Send, Cancel, Accept, Revert,
 *   Try again) so the list moves at once; the poll picks up from there.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getEditMessage, isEditorDisabledError, listEditMessages, readEditorError, EDITOR_MESSAGES_MAX_LIMIT } from '../../../../api/editor';
import type { EditMessage, EditorApiError, EditRun } from '../../../../api/editor';
import type { BuilderSource } from '../../../../api/builder';
import { isPolledMessage, isWorkingState, messageState } from './editModel';
import type { EditMessageState } from './editModel';

/** 4.10: the panel polls the active message every 5 seconds. */
export const EDIT_MESSAGE_POLL_MS = 5000;

/** At most this many list pages are read (200 rows each; a draft holds 30 messages by default). */
const MAX_PAGES = 10;

/** One message with what the panel has read about it beyond the summary. */
export interface EditMessageEntry {
  message: EditMessage;
  /** The builder's run summary, once a detail read or an action returned it. */
  run: EditRun | null;
  /** The attachments, once a detail read returned them. */
  sources: BuilderSource[] | null;
}

export interface UseEditMessagesOptions {
  /** false: ask nothing (default true). */
  enabled?: boolean;
  /** Poll interval in ms (default 5000). */
  intervalMs?: number;
  /** A polled message stopped working (see the file header). */
  onSettled?: (message: EditMessage, previous: EditMessageState) => void;
}

export interface EditMessagesState {
  entries: EditMessageEntry[];
  /** true until the first list read has answered. */
  loading: boolean;
  /** The last list read's failure, or null. */
  error: EditorApiError | null;
  /** The message being polled, or null. */
  activeId: string | null;
  /** Read the list again. */
  refresh: () => void;
  /** Put an action's answer into the list (new or replaced, ordinal order kept). */
  upsert: (message: EditMessage, run?: EditRun | null, sources?: BuilderSource[] | null) => void;
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function byOrdinal(a: EditMessageEntry, b: EditMessageEntry): number {
  return (a.message.ordinal ?? 0) - (b.message.ordinal ?? 0);
}

function merge(
  list: EditMessageEntry[],
  message: EditMessage,
  run?: EditRun | null,
  sources?: BuilderSource[] | null
): EditMessageEntry[] {
  const i = list.findIndex((e) => e.message.id === message.id);
  if (i < 0) return [...list, { message, run: run ?? null, sources: sources ?? null }].sort(byOrdinal);
  const old = list[i];
  const next: EditMessageEntry = {
    message,
    run: run === undefined ? old.run : run,
    sources: sources === undefined ? old.sources : sources,
  };
  return list.map((e, j) => (j === i ? next : e));
}

/** The list and the active message's polling; see the file header. */
export function useEditMessages(versionId: string | null | undefined, options: UseEditMessagesOptions = {}): EditMessagesState {
  const { enabled = true, intervalMs = EDIT_MESSAGE_POLL_MS, onSettled } = options;
  const [entries, setEntries] = useState<EditMessageEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(versionId) && enabled);
  const [error, setError] = useState<EditorApiError | null>(null);
  const [tick, setTick] = useState(0);
  const [visible, setVisible] = useState<boolean>(pageVisible);
  const entriesRef = useRef<EditMessageEntry[]>([]);
  entriesRef.current = entries;
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;
  const resumeRef = useRef(false);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const upsert = useCallback((message: EditMessage, run?: EditRun | null, sources?: BuilderSource[] | null) => {
    if (!message || !message.id) return;
    setEntries((list) => {
      const next = merge(list, message, run, sources);
      entriesRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onChange = () => {
      const now = pageVisible();
      if (now) resumeRef.current = true;
      setVisible(now);
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  // The list.
  useEffect(() => {
    if (!versionId || !enabled) {
      setEntries([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const items: EditMessage[] = [];
        let offset = 0;
        for (let page = 0; page < MAX_PAGES; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const res = await listEditMessages(versionId, { limit: EDITOR_MESSAGES_MAX_LIMIT, offset });
          if (cancelled) return;
          items.push(...res.items);
          if (!res.page || !res.page.hasMore || res.items.length === 0) break;
          offset += res.items.length;
        }
        if (cancelled) return;
        setEntries((prev) => {
          const kept = new Map(prev.map((e) => [e.message.id, e]));
          const next = items
            .filter((m) => m && m.id)
            .map((m) => {
              const old = kept.get(m.id);
              return { message: m, run: old ? old.run : null, sources: old ? old.sources : null };
            })
            .sort(byOrdinal);
          entriesRef.current = next;
          return next;
        });
        setError(null);
      } catch (err) {
        if (!cancelled) setError(readEditorError(err, 'The messages of this draft could not be loaded.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [versionId, enabled, tick]);

  // A message with documents shows its files (8.3): one detail read per such
  // message that no poll or action has read yet (a failure is not retried).
  const sourcesAskedRef = useRef<Set<string>>(new Set());
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!enabled) return;
    entries
      .filter((e) => e.message.has_material && e.sources === null && !isPolledMessage(e.message))
      .filter((e) => !sourcesAskedRef.current.has(e.message.id))
      .forEach((e) => {
        const id = e.message.id;
        sourcesAskedRef.current.add(id);
        getEditMessage(id)
          .then((detail) => {
            if (!aliveRef.current || !detail) return;
            setEntries((list) => {
              const i = list.findIndex((x) => x.message.id === id);
              if (i < 0) return list;
              const next = list.map((x, j) => (j === i ? { ...x, run: x.run ?? detail.run ?? null, sources: detail.sources ?? [] } : x));
              entriesRef.current = next;
              return next;
            });
          })
          .catch(() => {
            // The card simply shows no file list.
          });
      });
  }, [entries, enabled]);

  const activeId = useMemo(() => {
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      if (isPolledMessage(entries[i].message)) return entries[i].message.id;
    }
    return null;
  }, [entries]);

  // The active message's poll.
  useEffect(() => {
    if (!activeId || !enabled || !visible) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const read = async () => {
      try {
        const detail = await getEditMessage(activeId);
        if (cancelled || !detail || !detail.message) return;
        const previousEntry = entriesRef.current.find((e) => e.message.id === activeId) ?? null;
        const previous = previousEntry ? messageState(previousEntry.message) : null;
        const next = messageState(detail.message);
        setEntries((list) => {
          const merged = merge(list, detail.message, detail.run ?? null, detail.sources ?? null);
          entriesRef.current = merged;
          return merged;
        });
        if (previous !== null && previous !== next && isWorkingState(previous) && !isWorkingState(next)) {
          const cb = settledRef.current;
          if (typeof cb === 'function') cb(detail.message, previous);
        }
        if (isPolledMessage(detail.message)) timer = setTimeout(read, intervalMs);
      } catch (err) {
        if (cancelled) return;
        const problem = readEditorError(err, 'The message could not be read.');
        // A message that is gone, or a switch that is off, is not asked again; a blip is.
        const final = problem.status === 404 || problem.status === 403 || isEditorDisabledError(err);
        if (!final) timer = setTimeout(read, intervalMs);
      }
    };

    const delay = resumeRef.current ? 0 : intervalMs;
    resumeRef.current = false;
    timer = setTimeout(read, delay);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeId, enabled, visible, intervalMs]);

  return { entries, loading, error, activeId, refresh, upsert };
}

export default useEditMessages;
